import { useState, type ReactNode } from 'react';
import { Button, DataState, Dialog, Icon, PolicyExplain, Skeleton, type DataStateKind } from '@etnas/design-system';
import type { DataQuery } from '@/lib/paging';
import { ApiError, NetworkError, type PolicyDecision } from '@/api';
import { Modal } from './Modal';
import { relative } from '@/lib/format';

export interface StateAction {
  label: string;
  onClick: () => void;
  icon?: string;
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger';
}

const INLINE_TONE: Record<string, string> = {
  partial: 'urgent', stale: 'urgent', error: 'critical', conflict: 'urgent', corrected: 'urgent', restricted: 'sensitive', offline: 'urgent', downtime: 'urgent',
  success: 'success', loading: 'neutral', empty: 'neutral', filtered: 'neutral', validation: 'urgent', unauthorized: 'neutral', 'external-pending': 'neutral', 'entered-in-error': 'muted',
};
const INLINE_ICON: Record<string, string> = {
  partial: 'layers', stale: 'clock', error: 'critical', conflict: 'merge', corrected: 'history', restricted: 'shield', offline: 'offline', downtime: 'offline', success: 'done',
  loading: 'refresh', empty: 'info', filtered: 'filter', validation: 'urgent', unauthorized: 'lock', 'external-pending': 'hourglass', 'entered-in-error': 'x',
};

/**
 * Design-system DataState with working actions. Block variant wraps <DataState>; inline variant uses the
 * same `ex-dstate-inline` markup but with real buttons (the DS inline variant's action is display-only).
 */
export function StateBlock({
  kind, title, body, actions = [], inline, children,
}: { kind: DataStateKind; title?: string; body?: string; actions?: StateAction[]; inline?: boolean; children?: ReactNode }) {
  if (inline) {
    const tone = INLINE_TONE[kind] ?? 'neutral';
    const soft = ['critical', 'urgent', 'success', 'sensitive'].includes(tone) ? `var(--${tone}-soft)` : 'var(--surface-sunken)';
    return (
      <div className={`ex-dstate-inline ex-tone-${tone}`} role={kind === 'error' ? 'alert' : 'status'}>
        <Icon name={INLINE_ICON[kind] ?? 'info'} size={14} knockout={soft} />
        <b>{title}</b>
        {body ? <span className="ex-dstate-body">{body}</span> : null}
        {children}
        {actions.map((a) => (
          <button key={a.label} type="button" className="ex-linkbtn" onClick={a.onClick}>
            {a.label}
          </button>
        ))}
      </div>
    );
  }
  return (
    <DataState kind={kind} title={title} body={body} actions={[]}>
      {children}
      {actions.length ? (
        <div className="ex-row-gap ex-wrap">
          {actions.map((a, i) => (
            <Button key={a.label} size="sm" icon={a.icon} variant={a.variant ?? (i === 0 ? 'secondary' : 'quiet')} onClick={a.onClick}>
              {a.label}
            </Button>
          ))}
        </div>
      ) : null}
    </DataState>
  );
}

/** "Why can't I?" — opens PolicyExplain for a server decision. */
export function PolicyWhy({ policy, label = 'Why?' }: { policy: PolicyDecision; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="quiet" icon="info" onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open ? (
        <Modal onClose={() => setOpen(false)}>
          <Dialog title="How this decision was made" eyebrow="Access policy" onClose={() => setOpen(false)} width={560} footer={<Button onClick={() => setOpen(false)}>Close</Button>}>
            <PolicyExplain
              decision={policy.decision}
              action={policy.action}
              subject={policy.subject ?? 'You'}
              inputs={policy.inputs}
              obligations={policy.obligations}
              policy={policy.policyVersion}
              trace={policy.correlationId}
            />
            {policy.reason ? <p className="ex-small ex-fg-muted" style={{ margin: 0 }}>{policy.reason}</p> : null}
          </Dialog>
        </Modal>
      ) : null}
    </>
  );
}

/** Map any thrown error to the right data state (never collapse to "empty"). */
export function ErrorState({ error, onRetry, what = 'this information', inline }: { error: unknown; onRetry?: () => void; what?: string; inline?: boolean }) {
  const retry = onRetry ? [{ label: 'Retry', onClick: onRetry, icon: 'refresh' }] : [];
  if (error instanceof NetworkError) {
    return <StateBlock inline={inline} kind="offline" title="You are offline" body={`${capital(what)} could not load. Drafts on this device are kept; signing, ordering and release wait for the connection.`} actions={retry} />;
  }
  if (error instanceof ApiError) {
    if (error.code === 'ETNAS_FORBIDDEN') {
      return (
        <StateBlock inline={inline} kind="unauthorized" title={`${capital(what)} is outside your current role`} body={error.detail ?? 'Your administrator can explain which role grants it.'}>
          {error.policy ? <PolicyWhy policy={error.policy} /> : null}
        </StateBlock>
      );
    }
    if (error.code === 'ETNAS_FEATURE_DISABLED') {
      const downtime = error.problem.feature === 'downtime';
      return <StateBlock inline={inline} kind={downtime ? 'downtime' : 'unauthorized'} title={downtime ? 'Read-only during downtime' : 'Turned off for your organization'} body={error.detail} actions={retry} />;
    }
    if (error.code === 'ETNAS_NOT_FOUND') {
      return <StateBlock inline={inline} kind="empty" title="Not found" body={error.detail ?? `${capital(what)} does not exist or is not available to you.`} />;
    }
    if (error.code === 'ETNAS_UNAUTHENTICATED') {
      return <StateBlock inline={inline} kind="unauthorized" title="Sign in again to continue" body="Your session ended. Your place is kept." />;
    }
    return <StateBlock inline={inline} kind="error" title={`${capital(what)} could not load`} body={`${error.detail ?? error.title} Your drafts are safe. Reference ${error.reference}.`} actions={retry} />;
  }
  return <StateBlock inline={inline} kind="error" title={`${capital(what)} could not load`} body={error instanceof Error ? error.message : String(error)} actions={retry} />;
}

function capital(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Renders a TanStack query through the data-state rules: loading (skeleton in the real layout), error (typed),
 * empty (says what was searched), stale (age + refresh) and refetch failure over existing data (partial).
 */
export function QueryView<T>({
  q, what, children, isEmpty, empty, skeleton, staleAfterMs, inlineError,
}: {
  q: DataQuery<T>;
  what: string;
  children: (data: T) => ReactNode;
  isEmpty?: (data: T) => boolean;
  empty?: { title: string; body?: string; actions?: StateAction[] };
  skeleton?: { rows?: number; cols?: number[] };
  staleAfterMs?: number;
  inlineError?: boolean;
}) {
  if (q.isPending) {
    return (
      <div role="status" aria-busy="true" aria-label={`Loading ${what}`} style={{ padding: '4px 0' }}>
        <Skeleton rows={skeleton?.rows ?? 4} cols={skeleton?.cols ?? [38, 22, 26]} />
        <span className="ex-visually-hidden">Loading {what}</span>
      </div>
    );
  }
  if (q.isError && q.data === undefined) return <ErrorState error={q.error} onRetry={() => q.refetch()} what={what} inline={inlineError} />;
  const data = q.data as T;
  if (isEmpty?.(data)) {
    return <StateBlock kind="empty" title={empty?.title ?? `No ${what}`} body={empty?.body} actions={empty?.actions} />;
  }
  const stale = staleAfterMs && q.dataUpdatedAt && Date.now() - q.dataUpdatedAt > staleAfterMs;
  return (
    <>
      {q.isError ? <StateBlock inline kind="partial" title="Showing the last loaded copy" body={`Refreshing ${what} failed ${relative(new Date(q.errorUpdatedAt))}.`} actions={[{ label: 'Retry', onClick: () => q.refetch() }]} /> : null}
      {stale ? <StateBlock inline kind="stale" title={`Loaded ${relative(new Date(q.dataUpdatedAt))}`} body="Values may be out of date." actions={[{ label: 'Refresh now', onClick: () => q.refetch() }]} /> : null}
      {children(data)}
    </>
  );
}

/**
 * Error from a command (mutation): names the problem, lists field issues, offers "Why?" for policy denials.
 * Version conflicts and step-up cancellations are handled by callers; this is the generic fallback.
 */
export function ActionError({ error }: { error: unknown }) {
  if (!error) return null;
  if (error instanceof NetworkError) {
    return <StateBlock inline kind="offline" title="Not sent — you are offline" body="Nothing was changed on the server. Try again when the connection returns." />;
  }
  if (error instanceof ApiError) {
    const kind: DataStateKind =
      error.code === 'ETNAS_VALIDATION' || error.code === 'ETNAS_SAFETY_CHECK' ? 'validation'
        : error.code === 'ETNAS_FORBIDDEN' ? 'unauthorized'
          : error.code === 'ETNAS_VERSION_CONFLICT' ? 'conflict'
            : error.code === 'ETNAS_FEATURE_DISABLED' ? 'downtime'
              : 'error';
    const issues = error.fieldErrors.filter((f) => f.message || f.code);
    return (
      <div role="alert" className="ex-stack is-tight">
        <StateBlock inline kind={kind} title={error.title} body={[error.detail, error.code === 'ETNAS_STEP_UP_REQUIRED' ? 'Identity check was cancelled; nothing was changed.' : '', kind === 'error' ? `Reference ${error.reference}.` : ''].filter(Boolean).join(' ')}>
          {error.policy ? <PolicyWhy policy={error.policy} /> : null}
        </StateBlock>
        {issues.length ? (
          <ul className="ex-help" style={{ margin: 0, paddingLeft: 18 }}>
            {issues.map((f, i) => (
              <li key={i}>
                <b>{f.path}</b>: {f.message ?? f.code}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }
  return <StateBlock inline kind="error" title="Something went wrong" body={error instanceof Error ? error.message : String(error)} />;
}
